import { useEffect, useRef, useState } from "react";
import { api, AUD, AUDc } from "../lib/api";
import { ArrowClockwise, Plus, Trash, Info, X, Check, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isDemoMode } from "../lib/demoStore";

const EMPTY_ROW = { symbol: "", exchange: "ASX", units: "", name: "" };

// Small helpers for the source-of-price badge shown next to each holding.
// - asx-markit: authoritative AU feed (asx.com.au uses same source)
// - yahoo: Yahoo Finance regular market price (US primary, ASX fallback)
// - yahoo-stale: Yahoo's YHD historical-only feed (least trusted)
// - cache: value served from our 15-min price cache
function sourceLabel(src) {
    if (src === "asx-markit") return "ASX";
    if (src === "yahoo") return "Y";
    if (src === "yahoo-stale") return "Y!";
    if (src === "coingecko") return "CG";
    return src?.slice(0, 3).toUpperCase() || "?";
}
function sourceHint(src) {
    if (src === "asx-markit") return "ASX Markit Digital — authoritative AU feed (~20 min delayed)";
    if (src === "yahoo") return "Yahoo Finance regular market price";
    if (src === "yahoo-stale") return "Yahoo YHD historical feed — verify against your broker";
    if (src === "coingecko") return "CoinGecko crypto price";
    return src;
}
function sourceBadgeStyle(src) {
    if (src === "asx-markit") return { background: "var(--eucalyptus)", color: "var(--bone)" };
    if (src === "yahoo-stale") return { background: "var(--terracotta)", color: "var(--bone)" };
    return { background: "rgba(0,0,0,0.05)", color: "var(--muted)" };
}

/** Modal: add multiple tickers linked to a portfolio */
function TickersWizard({ portfolio, onClose, onSaved }) {
    const [rows, setRows] = useState([{ ...EMPTY_ROW }, { ...EMPTY_ROW }, { ...EMPTY_ROW }]);
    const [busy, setBusy] = useState(false);

    const updateRow = (idx, patch) => {
        setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
    };
    const addRow = () => setRows(rs => [...rs, { ...EMPTY_ROW }]);
    const removeRow = (idx) => setRows(rs => rs.filter((_, i) => i !== idx));

    const submit = async (e) => {
        e.preventDefault();
        const nonEmpty = rows.filter(r => r.symbol.trim() || String(r.units).trim());
        if (nonEmpty.length === 0) {
            toast.warning("Add at least one ticker with a symbol and units.");
            return;
        }
        // Row-level validation to tell the user exactly which row is wrong.
        for (let i = 0; i < nonEmpty.length; i++) {
            const r = nonEmpty[i];
            if (!r.symbol.trim()) {
                toast.warning(`Row ${i + 1}: symbol is empty.`);
                return;
            }
            if (!(Number(r.units) > 0)) {
                toast.warning(`Row ${i + 1} (${r.symbol.trim().toUpperCase()}): units must be greater than 0.`);
                return;
            }
        }
        const cleaned = nonEmpty.map(r => ({
            entity: portfolio.entity,
            portfolio_ref: portfolio.portfolio,
            symbol: r.symbol.trim().toUpperCase(),
            exchange: r.exchange,
            units: Number(r.units),
            name: r.name.trim(),
        }));
        setBusy(true);
        try {
            const { data } = await api.post("/holdings/bulk", { holdings: cleaned });
            toast.success(`Added ${data.inserted} ticker${data.inserted === 1 ? "" : "s"} to ${portfolio.portfolio}. Click Refresh prices to fetch live values.`);
            onSaved();
            onClose();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail
                : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(", ")
                : err?.message || "Failed to add tickers. Check the entity and symbol values.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(0,0,0,0.4)" }}
             onClick={onClose}
             data-testid="tickers-wizard-modal">
            <div className="bg-white rounded max-w-3xl w-full max-h-[90vh] overflow-auto shadow-xl"
                 onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between p-6 border-b"
                     style={{ borderColor: "var(--line)" }}>
                    <div>
                        <div className="label-eyebrow">Add tickers</div>
                        <h2 className="font-serif text-2xl mt-1">{portfolio.portfolio}</h2>
                        <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                            {portfolio.entity} · Baseline value {AUD(portfolio.value)}. Enter the
                            underlying tickers + units held inside this portfolio for live pricing.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-[var(--muted)] hover:text-[var(--ink)]"
                            data-testid="tickers-wizard-close">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={submit} className="p-6">
                    <div className="space-y-3">
                        <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-widest"
                             style={{ color: "var(--muted)" }}>
                            <div className="col-span-3">Symbol</div>
                            <div className="col-span-2">Exchange</div>
                            <div className="col-span-2 text-right">Units</div>
                            <div className="col-span-4">Name (optional)</div>
                            <div className="col-span-1"></div>
                        </div>
                        {rows.map((r, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 items-center"
                                 data-testid={`wizard-row-${i}`}>
                                <input
                                    value={r.symbol}
                                    onChange={e => updateRow(i, { symbol: e.target.value.toUpperCase() })}
                                    placeholder="VAS"
                                    className="col-span-3 border border-[var(--line)] px-3 py-2 rounded text-sm"
                                    data-testid={`wizard-symbol-${i}`}
                                />
                                <select
                                    value={r.exchange}
                                    onChange={e => updateRow(i, { exchange: e.target.value })}
                                    className="col-span-2 border border-[var(--line)] px-3 py-2 rounded text-sm">
                                    <option>ASX</option>
                                    <option>US</option>
                                    <option>CRYPTO</option>
                                </select>
                                <input
                                    type="number"
                                    step="any"
                                    value={r.units}
                                    onChange={e => updateRow(i, { units: e.target.value })}
                                    placeholder="0"
                                    className="col-span-2 border border-[var(--line)] px-3 py-2 rounded text-sm text-right font-mono-num"
                                    data-testid={`wizard-units-${i}`}
                                />
                                <input
                                    value={r.name}
                                    onChange={e => updateRow(i, { name: e.target.value })}
                                    placeholder="e.g. Australian Shares Index ETF"
                                    className="col-span-4 border border-[var(--line)] px-3 py-2 rounded text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeRow(i)}
                                    disabled={rows.length === 1}
                                    className="col-span-1 text-[var(--muted)] hover:text-[var(--terracotta)] disabled:opacity-30 justify-self-center"
                                    data-testid={`wizard-remove-${i}`}>
                                    <Trash size={14} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={addRow}
                        className="btn-ghost mt-4 text-xs flex items-center gap-2"
                        data-testid="wizard-add-row">
                        <Plus size={12} /> Add another row
                    </button>

                    <div className="flex justify-end gap-2 mt-6 pt-6 border-t"
                         style={{ borderColor: "var(--line)" }}>
                        <button type="button" onClick={onClose} className="btn-ghost text-sm">
                            Cancel
                        </button>
                        <button type="submit" disabled={busy}
                                className="btn-primary text-sm disabled:opacity-60"
                                data-testid="wizard-submit">
                            {busy ? "Saving…" : "Save tickers"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function Prices() {
    const [holdings, setHoldings] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [cash, setCash] = useState({ items: [], fx_aud_usd: 0 });
    const [cashDraftId, setCashDraftId] = useState(null);
    const [cashDraft, setCashDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const addFormRef = useRef(null);

    // When the form is opened, scroll it into view so users notice it — it renders
    // below the portfolio snapshot and cash-balances panels, easily below the fold.
    useEffect(() => {
        if (showAdd && addFormRef.current) {
            addFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [showAdd]);
    const [wizardFor, setWizardFor] = useState(null); // portfolio object
    const [form, setForm] = useState({ entity: isDemoMode() ? "SMSF" : "Pendaz", symbol: "", exchange: "ASX", units: 0, name: "", portfolio_ref: "", owner: "Joint" });

    const load = async () => {
        const [h, s, c] = await Promise.all([
            api.get("/holdings"),
            api.get("/portfolios/snapshot"),
            api.get("/portfolio-cash"),
        ]);
        setHoldings(h.data);
        setSnapshot(s.data);
        setCash(c.data);
    };
    useEffect(() => { load(); }, []);

    // ---- cash helpers ----
    const cashFor = (portfolioName) =>
        cash.items.filter(c => c.portfolio === portfolioName);

    const startEditCash = (row) => {
        setCashDraftId(row.id);
        setCashDraft({ balance: row.balance, notes: row.notes || "" });
    };
    const cancelEditCash = () => { setCashDraftId(null); setCashDraft(null); };
    const saveCash = async (row) => {
        try {
            await api.put(`/portfolio-cash/${row.id}`, {
                portfolio: row.portfolio, label: row.label,
                currency: row.currency, balance: Number(cashDraft.balance) || 0,
                notes: cashDraft.notes || "",
            });
            toast.success(`${row.label} updated`);
            cancelEditCash();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const cashPortfolioTotalAud = (portfolioName) =>
        cashFor(portfolioName).reduce((s, c) => s + (c.balance_aud || 0), 0);

    const refresh = async () => {
        if (holdings.length === 0) {
            toast.info("Add a holding first — click a portfolio row to launch the tickers wizard.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/holdings/refresh");
            toast.success(`Updated ${data.updated} of ${data.total} holdings`);
            const errs = data.errors || [];
            if (errs.length) {
                // Group by exchange for a clearer hint
                const mislabelled = errs.filter(
                    e => e.exchange === "US" && e.hint);
                if (mislabelled.length) {
                    toast.warning(
                        `${mislabelled.map(e => e.symbol).join(", ")} not found on US — likely ASX ETFs. Use the "Fix to ASX" button on the row, or delete and re-add with exchange=ASX.`,
                        { duration: 10000 }
                    );
                }
                const others = errs.filter(e => !mislabelled.includes(e));
                if (others.length) {
                    toast.warning(
                        `${others.length} other symbol${others.length === 1 ? "" : "s"} had issues: ${others.map(e => e.symbol).join(", ")}`,
                        { duration: 7000 }
                    );
                }
            }
            await load();
        } catch {
            toast.error("Refresh failed — the price service may be temporarily unavailable. Please try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    const fixToAsx = async (h) => {
        try {
            await api.patch(`/holdings/${h.id}/exchange`, { exchange: "ASX" });
            toast.success(`${h.symbol} now tagged ASX — click Refresh to fetch live price.`);
            await load();
        } catch {
            toast.error(`Failed to re-tag ${h.symbol}`);
        }
    };

    const add = async (e) => {
        e.preventDefault();
        try {
            await api.post("/holdings", { ...form, units: Number(form.units) });
            toast.success(`Added ${form.symbol} (${form.units} units) — click Refresh prices to fetch live data.`);
            setShowAdd(false);
            setForm({ entity: isDemoMode() ? "SMSF" : "Pendaz", symbol: "", exchange: "ASX", units: 0, name: "", portfolio_ref: "", owner: "Joint" });
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail
                : Array.isArray(detail) ? detail.map(d => d.msg || d).join(", ")
                : `Failed to add ${form.symbol || "holding"}. Check the entity + symbol values.`);
        }
    };

    const del = async (id) => {
        await api.delete(`/holdings/${id}`);
        await load();
    };

    const liveTotal = holdings.reduce(
        (a, h) => a + (h.last_price || 0) * (h.units || 0), 0);
    const workbookTotal = snapshot?.total_workbook_value || 0;
    const portfolios = snapshot?.portfolios || [];

    return (
        <div data-testid="prices-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Live Market Prices</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Portfolio, live.</h1>
                    <p className="text-[var(--muted)] mt-3 max-w-2xl text-sm">
                        <strong>Click any portfolio row</strong> to open the tickers wizard and enter
                        the underlying holdings — ASX prices come from <strong>ASX Markit Digital</strong>{" "}
                        (same feed as asx.com.au), US from <strong>Yahoo Finance</strong>, crypto from{" "}
                        <strong>CoinGecko</strong>. All feeds are <strong>~20 minute delayed</strong>{" "}
                        — not for intraday trading decisions.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button className="btn-ghost flex items-center gap-2 text-sm"
                            onClick={() => setShowAdd(!showAdd)} data-testid="add-holding-btn">
                        <Plus size={14} /> Add single holding
                    </button>
                    <button className="btn-primary flex items-center gap-2 text-sm disabled:opacity-60"
                            onClick={refresh} disabled={busy || holdings.length === 0}
                            data-testid="refresh-prices-btn">
                        <ArrowClockwise size={14} className={busy ? "animate-spin" : ""} />
                        {busy ? "Fetching…" : "Refresh prices"}
                    </button>
                </div>
            </div>

            {/* Portfolio snapshot */}
            <div className="hairline bg-white p-8 rounded mb-8" data-testid="portfolio-snapshot">
                <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
                    <div>
                        <div className="label-eyebrow">Portfolio snapshot</div>
                        <h2 className="font-serif text-2xl mt-1">Baseline + Live overlay</h2>
                        <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                            {isDemoMode()
                                ? "SMSF financial-asset lines. Click a row to add its underlying tickers."
                                : "Pendaz & SMSF financial-asset lines. Click a row to add its underlying tickers."}
                        </p>
                    </div>
                    <div className="flex gap-8">
                        <div className="text-right">
                            <div className="label-eyebrow">Baseline total</div>
                            <div className="font-serif text-2xl mt-1 font-mono-num"
                                 data-testid="portfolio-total">{AUD(workbookTotal)}</div>
                        </div>
                        {snapshot?.total_live_value > 0 && (
                            <div className="text-right">
                                <div className="label-eyebrow">Live total</div>
                                <div className="font-serif text-2xl mt-1 font-mono-num"
                                     data-testid="portfolio-live-total">
                                    {AUD(snapshot.total_live_value)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="editorial w-full" data-testid="portfolios-table">
                        <thead>
                            <tr>
                                <th>Entity</th>
                                <th>Portfolio</th>
                                <th>Liquidity</th>
                                <th className="num">Baseline</th>
                                <th className="num">Live tickers</th>
                                <th className="num">Live cash</th>
                                <th className="num">Live total</th>
                                <th className="num">Delta</th>
                            </tr>
                        </thead>
                        <tbody>
                            {portfolios.map((p, i) => (
                                <tr key={i}
                                    className="cursor-pointer hover:bg-[var(--line)]/30"
                                    onClick={() => setWizardFor(p)}
                                    data-testid={`portfolio-row-${i}`}>
                                    <td className="text-[var(--muted)] font-mono text-xs uppercase tracking-widest">
                                        {p.entity}
                                    </td>
                                    <td className="font-semibold underline-offset-2 hover:underline">
                                        {p.portfolio}
                                    </td>
                                    <td className="text-sm text-[var(--muted)]">{p.liquidity}</td>
                                    <td className="num font-mono-num font-medium">{AUD(p.value)}</td>
                                    <td className="num font-mono-num"
                                        style={{ color: (p.live_tickers || 0) > 0 ? "var(--ink)" : "var(--muted)" }}>
                                        {(p.live_tickers || 0) > 0 ? AUD(p.live_tickers) : "—"}
                                    </td>
                                    <td className="num font-mono-num"
                                        style={{ color: (p.live_cash || 0) > 0 ? "var(--ink)" : "var(--muted)" }}>
                                        {(p.live_cash || 0) > 0 ? AUD(p.live_cash) : "—"}
                                    </td>
                                    <td className="num font-mono-num font-medium"
                                        style={{ color: p.has_live ? "var(--ink)" : "var(--muted)" }}>
                                        {p.has_live ? AUD(p.live_value) : "—"}
                                    </td>
                                    <td className="num font-mono-num"
                                        style={{ color: p.delta > 0 ? "var(--eucalyptus)"
                                                     : p.delta < 0 ? "var(--terracotta)" : "var(--muted)" }}>
                                        {p.has_live ? `${p.delta >= 0 ? "+" : ""}${AUD(p.delta)}` : "—"}
                                    </td>
                                </tr>
                            ))}
                            {portfolios.length === 0 && (
                                <tr><td colSpan={8} className="p-6 text-center text-[var(--muted)]">
                                    No portfolio lines found in balance sheet.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Portfolio Cash — inline editable balances per portfolio */}
            <div className="hairline bg-white p-8 rounded mb-8" data-testid="portfolio-cash-panel">
                <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
                    <div>
                        <div className="label-eyebrow">Portfolio cash</div>
                        <h2 className="font-serif text-2xl mt-1">Cash balances by portfolio</h2>
                        <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                            Manual cash line items (broker sweep, cash management accounts, etc.).
                            Click the pencil to update a balance — USD is converted to AUD using the
                            live AUDUSD rate.
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="label-eyebrow">AUDUSD</div>
                        <div className="font-serif text-2xl mt-1 font-mono-num"
                             data-testid="fx-aud-usd">
                            {cash.fx_aud_usd ? cash.fx_aud_usd.toFixed(4) : "—"}
                        </div>
                        <div className="text-xs text-[var(--muted)]">1 AUD = X USD</div>
                    </div>
                </div>

                {[...new Set(cash.items.map(c => c.portfolio))].sort().map(pName => {
                    const rows = cashFor(pName);
                    const totalAud = cashPortfolioTotalAud(pName);
                    return (
                        <div key={pName} className="mb-6 last:mb-0"
                             data-testid={`cash-portfolio-${pName.replace(/\s+/g, "-")}`}>
                            <div className="flex items-baseline justify-between mb-2">
                                <h3 className="font-serif text-lg">{pName}</h3>
                                <div className="text-sm">
                                    <span className="label-eyebrow">Total (AUD)</span>{" "}
                                    <span className="font-mono-num font-medium ml-2">{AUD(totalAud)}</span>
                                </div>
                            </div>
                            <div className="overflow-x-auto -mx-2 px-2">
                            <table className="editorial w-full text-sm min-w-[520px]">
                                <thead>
                                    <tr>
                                        <th>Label</th>
                                        <th>Ccy</th>
                                        <th className="num">Balance</th>
                                        <th className="num">AUD equiv.</th>
                                        <th>Notes</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <tr key={row.id} data-testid={`cash-row-${row.id}`}>
                                            <td className="font-medium">{row.label}</td>
                                            <td>
                                                <span className="text-xs px-2 py-0.5 rounded"
                                                      style={{
                                                          background: row.currency === "USD"
                                                              ? "var(--eucalyptus)" : "var(--line)",
                                                          color: row.currency === "USD" ? "white" : "var(--ink)"
                                                      }}>
                                                    {row.currency}
                                                </span>
                                            </td>
                                            <td className="num font-mono-num">
                                                {cashDraftId === row.id ? (
                                                    <input type="number" step="0.01"
                                                           value={cashDraft.balance}
                                                           onChange={e => setCashDraft(d => ({ ...d, balance: e.target.value }))}
                                                           className="border border-[var(--line)] px-2 py-1 rounded w-32 text-right"
                                                           data-testid={`cash-balance-input-${row.id}`}
                                                           autoFocus />
                                                ) : (
                                                    AUDc(row.balance)
                                                )}
                                            </td>
                                            <td className="num font-mono-num" style={{ color: "var(--muted)" }}>
                                                {row.currency === "USD" ? AUDc(row.balance_aud) : "—"}
                                            </td>
                                            <td className="text-[var(--muted)] text-xs">
                                                {cashDraftId === row.id ? (
                                                    <input type="text" value={cashDraft.notes}
                                                           onChange={e => setCashDraft(d => ({ ...d, notes: e.target.value }))}
                                                           className="border border-[var(--line)] px-2 py-1 rounded w-full"
                                                           placeholder="Optional notes" />
                                                ) : (
                                                    row.notes || "—"
                                                )}
                                            </td>
                                            <td>
                                                {cashDraftId === row.id ? (
                                                    <div className="flex gap-2 justify-end">
                                                        <button onClick={() => saveCash(row)}
                                                                className="text-[var(--eucalyptus)] hover:opacity-80"
                                                                data-testid={`cash-save-${row.id}`}>
                                                            <Check size={16} />
                                                        </button>
                                                        <button onClick={cancelEditCash}
                                                                className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                                data-testid={`cash-cancel-${row.id}`}>
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button onClick={() => startEditCash(row)}
                                                            className="text-[var(--muted)] hover:text-[var(--ink)]"
                                                            data-testid={`cash-edit-${row.id}`}>
                                                        <PencilSimple size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    );
                })}
                {cash.items.length === 0 && (
                    <div className="text-sm text-[var(--muted)] italic py-4">
                        No cash lines yet — they&apos;ll appear here once seeded.
                    </div>
                )}
            </div>

            {/* Live holdings section */}
            <div className="mb-4 flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow">Live holdings</div>
                    <h2 className="font-serif text-2xl mt-1">Per-ticker positions</h2>
                </div>
                <div className="text-right">
                    <div className="label-eyebrow">Live total</div>
                    <div className="font-serif text-2xl mt-1 font-mono-num"
                         data-testid="live-holdings-total">
                        {AUD(liveTotal)}
                    </div>
                </div>
            </div>

            {holdings.length === 0 && (
                <div className="hairline bg-white p-6 rounded mb-6 flex items-start gap-3 text-sm"
                     data-testid="live-holdings-hint"
                     style={{ background: "rgba(123, 143, 92, 0.08)" }}>
                    <Info size={18} className="mt-0.5 flex-shrink-0"
                          style={{ color: "var(--eucalyptus)" }} />
                    <div>
                        <div className="font-medium mb-1">No live holdings yet.</div>
                        <div style={{ color: "var(--muted)" }}>
                            <strong>Click a portfolio row</strong> above to open the tickers wizard,
                            or use <strong>Add single holding</strong>. Live prices via Yahoo Finance
                            (ASX + US) and CoinGecko (crypto) — no API key needed.
                        </div>
                    </div>
                </div>
            )}

            {showAdd && (
                <form ref={addFormRef} onSubmit={add} className="hairline bg-white p-6 rounded mb-6 grid md:grid-cols-6 gap-3 items-end"
                      data-testid="add-holding-form">
                    <div>
                        <div className="label-eyebrow mb-1">Entity</div>
                        <select value={form.entity} onChange={e => setForm({ ...form, entity: e.target.value })}
                                data-testid="holding-entity-select"
                                className="border border-[var(--line)] px-3 py-2 rounded text-sm w-full">
                            {isDemoMode()
                                ? <><option>Personal</option><option>SMSF</option></>
                                : <><option>Personal</option><option>Pendaz</option><option>SMSF</option></>}
                        </select>
                    </div>
                    {form.entity === "Personal" && (
                        <div>
                            <div className="label-eyebrow mb-1">Owner</div>
                            <select value={form.owner || "Joint"}
                                    onChange={e => setForm({ ...form, owner: e.target.value })}
                                    data-testid="holding-owner-select"
                                    className="border border-[var(--line)] px-3 py-2 rounded text-sm w-full">
                                {isDemoMode()
                                    ? <><option value="Darren">Partner A</option><option value="Penny">Partner B</option><option>Joint</option></>
                                    : <><option>Darren</option><option>Penny</option><option>Joint</option></>}
                            </select>
                        </div>
                    )}
                    <div>
                        <div className="label-eyebrow mb-1">Symbol</div>
                        <input required value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
                               placeholder="VOO" className="border border-[var(--line)] px-3 py-2 rounded text-sm w-full" />
                    </div>
                    <div>
                        <div className="label-eyebrow mb-1">Exchange</div>
                        <select value={form.exchange} onChange={e => setForm({ ...form, exchange: e.target.value })}
                                className="border border-[var(--line)] px-3 py-2 rounded text-sm w-full">
                            <option>ASX</option><option>US</option><option>CRYPTO</option>
                        </select>
                    </div>
                    <div>
                        <div className="label-eyebrow mb-1">Units</div>
                        <input required type="number" step="0.01" value={form.units}
                               onChange={e => setForm({ ...form, units: e.target.value })}
                               className="border border-[var(--line)] px-3 py-2 rounded text-sm w-full font-mono-num" />
                    </div>
                    {form.entity !== "Personal" && (
                        <div>
                            <div className="label-eyebrow mb-1">Portfolio (link)</div>
                            <select value={form.portfolio_ref}
                                    onChange={e => setForm({ ...form, portfolio_ref: e.target.value })}
                                    className="border border-[var(--line)] px-3 py-2 rounded text-sm w-full">
                                <option value="">— none —</option>
                                {portfolios.map((p, i) => (
                                    <option key={i} value={p.portfolio}>{p.portfolio}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <button type="submit" className="btn-primary text-sm">Add</button>
                </form>
            )}

            <div className="hairline bg-white rounded overflow-hidden">
                <div className="overflow-x-auto">
                <table className="editorial w-full" data-testid="holdings-table">
                    <thead>
                        <tr>
                            <th>Entity</th><th>Symbol</th><th>Portfolio</th><th>Exchange</th>
                            <th className="num">Units</th><th className="num">Last Price</th>
                            <th className="num">Market Value</th><th>Last Updated</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {holdings.map(h => (
                            <tr key={h.id}>
                                <td className="text-[var(--muted)] font-mono text-xs uppercase tracking-widest">
                                    {h.entity}
                                    {h.entity === "Personal" && h.owner && (
                                        <span className="ml-2 text-[10px] text-[var(--eucalyptus)] normal-case font-sans tracking-normal"
                                              data-testid={`holding-owner-${h.id}`}>
                                            · {isDemoMode()
                                                ? (h.owner === "Darren" ? "Partner A"
                                                   : h.owner === "Penny" ? "Partner B"
                                                   : h.owner)
                                                : h.owner}
                                        </span>
                                    )}
                                </td>
                                <td className="font-semibold">{h.symbol}</td>
                                <td className="text-sm text-[var(--muted)]">{h.portfolio_ref || "—"}</td>
                                <td className="text-[var(--muted)] text-sm">{h.exchange}</td>
                                <td className="num font-mono-num">{h.units}</td>
                                <td className="num font-mono-num">{h.last_price ? AUDc(h.last_price) : "—"}</td>
                                <td className="num font-mono-num font-medium">{h.last_price ? AUD(h.last_price * h.units) : "—"}</td>
                                <td className="text-[var(--muted)] text-xs">
                                    <div className="flex items-center gap-2">
                                        <span>{h.last_updated ? new Date(h.last_updated).toLocaleString("en-AU") : "Never"}</span>
                                        {h.last_source && (
                                            <span
                                                data-testid={`price-source-${h.symbol}`}
                                                title={sourceHint(h.last_source)}
                                                className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-mono"
                                                style={sourceBadgeStyle(h.last_source)}>
                                                {sourceLabel(h.last_source)}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="flex items-center gap-2 justify-end">
                                        {h.exchange === "US" && h.symbol.length <= 4 && !h.last_price && (
                                            <button onClick={() => fixToAsx(h)}
                                                    className="text-xs px-2 py-1 rounded border border-[var(--eucalyptus)] text-[var(--eucalyptus)] hover:bg-[var(--eucalyptus)] hover:text-white transition-colors"
                                                    title="Re-tag as ASX and refresh price"
                                                    data-testid={`fix-asx-${h.symbol}`}>
                                                Fix to ASX
                                            </button>
                                        )}
                                        <button onClick={() => del(h.id)} className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                data-testid={`delete-holding-${h.symbol}`}>
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                {holdings.length === 0 && (
                    <div className="p-10 text-center text-[var(--muted)] text-sm"
                         data-testid="holdings-empty">
                        No live holdings entered yet.
                    </div>
                )}
            </div>

            {wizardFor && (
                <TickersWizard
                    portfolio={wizardFor}
                    onClose={() => setWizardFor(null)}
                    onSaved={load}
                />
            )}
        </div>
    );
}
